import { ToolOutlined } from "@ant-design/icons";
import { Logo } from "@p0security/frontend-core/react";
import { parseFederatedSubject } from "@p0security/integrations-agentic-shared/federation";
import {
  type AgenticClientListItem,
  type AgenticClientPlatform,
  AgenticClientPlatforms,
} from "@p0security/integrations-agentic-shared/interface";
import { Flex } from "@p0security/ui";
import { isKeyOf } from "@p0security/util/types/is";
import { startCase } from "lodash";
import type { ReactNode } from "react";

/** Wikimedia-hosted, like the MCP logo in the integration registry; external
 * to P0 to avoid copyright issues. */
const CLAUDE_ICON_URL =
  "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b0/Claude_AI_symbol.svg/250px-Claude_AI_symbol.svg.png";

const claudeIcon = <Logo logo={CLAUDE_ICON_URL} size={14} title="Claude" />;
const customIcon = <ToolOutlined aria-hidden="true" />;

const PLATFORMS = {
  "claude-agents": { icon: claudeIcon, label: "Claude Agents SDK" },
  "claude-code": { icon: claudeIcon, label: "Claude Code" },
  custom: { icon: customIcon, label: "Custom agent" },
} satisfies Record<AgenticClientPlatform, { icon: ReactNode; label: string }>;

export const MCP_CLIENT_PLATFORM_OPTIONS = AgenticClientPlatforms.map(
  (value) => ({
    label: (
      <Flex align="center" gap={8}>
        {PLATFORMS[value].icon}
        {PLATFORMS[value].label}
      </Flex>
    ),
    value,
  })
);

/** Returns a user-facing label for a known or legacy MCP client platform. */
export const formatMcpClientPlatform = (platform: string) =>
  isKeyOf(PLATFORMS, platform)
    ? PLATFORMS[platform].label
    : startCase(platform);

/** Returns the registered name for a confidential or federated MCP client. */
export const defaultMcpClientName = (client: AgenticClientListItem) =>
  client.type === "jwt_bearer"
    ? (parseFederatedSubject(client.id)?.name ?? client.id)
    : typeof client.hostname === "string" && client.hostname.trim()
      ? client.hostname.trim()
      : formatMcpClientPlatform(client.platform);

/** Returns the customer-facing name for an MCP client. */
export const formatMcpClientName = (client: AgenticClientListItem) =>
  client.displayName?.trim() || defaultMcpClientName(client);

/** Returns a saved display name for a matching client, falling back to its ID. */
export const mcpClientDisplayName = (
  clients: AgenticClientListItem[],
  clientId?: string
) =>
  clients.find((client) => client.id === clientId)?.displayName?.trim() ||
  clientId;

/** Returns an icon for a known platform; legacy platforms share the generic
 * custom-agent icon. */
export const mcpClientPlatformIcon = (platform: string): ReactNode =>
  isKeyOf(PLATFORMS, platform) ? PLATFORMS[platform].icon : customIcon;

/** Extracts the platform embedded in confidential MCP client IDs
 * (`user:platform:hex`). Headless client IDs are opaque hashes, so this
 * returns undefined for them and any other unrecognized format. */
export const mcpClientPlatformFromClientId = (
  clientId: string
): AgenticClientPlatform | undefined => {
  const segments = clientId.split(":");
  const candidate = segments[segments.length - 2];
  return candidate !== undefined && isKeyOf(PLATFORMS, candidate)
    ? candidate
    : undefined;
};

/** Platform icon for an MCP client ID; renders nothing when the ID does not
 * embed a known platform. */
export const McpClientIdIcon: React.FC<{ clientId?: string }> = ({
  clientId,
}) => {
  const platform = clientId
    ? mcpClientPlatformFromClientId(clientId)
    : undefined;
  return platform ? (
    <span aria-hidden="true" className="inline-flex shrink-0">
      {PLATFORMS[platform].icon}
    </span>
  ) : null;
};
